import {
  faCheckCircle,
  faExclamationCircle,
  faInfoCircle,
  faQuestionCircle,
  faTimes,
  faTimesCircle
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useMemo, useState } from 'react';

import type { Props } from './types';

const Levels = {
  DEFAULT: 'default',
  ERROR: 'error',
  INFO: 'info',
  SUCCESS: 'success',
  WARN: 'warn'
};

// Define the levelMapping object
const levelMapping = {
  [Levels.DEFAULT]: {
    text: 'text-gray-800',
    bg: 'bg-gray-50',
    border: 'border-gray-200',
    icon: faQuestionCircle
  },
  [Levels.ERROR]: {
    text: 'text-red-800',
    bg: 'bg-red-50',
    border: 'border-red-200',
    icon: faTimesCircle
  },
  [Levels.INFO]: {
    text: 'text-blue-800',
    bg: 'bg-blue-50',
    border: 'border-blue-200',
    icon: faInfoCircle
  },
  [Levels.SUCCESS]: {
    text: 'text-green-800',
    bg: 'bg-green-50',
    border: 'border-green-200',
    icon: faCheckCircle
  },
  [Levels.WARN]: {
    text: 'text-yellow-800',
    bg: 'bg-yellow-50',
    border: 'border-yellow-200',
    icon: faExclamationCircle
  }
};

/**
 * @deprecated Do not use this component. Use the base components at the root of the UI folder instead.
 */
export default function Alert({
  afterDismiss,
  children,
  level,
  message,
  dismissable = true
}: Props) {
  const [showAlert, setShowAlert] = useState(true);

  const backgroundColor = useMemo(() => {
    return levelMapping[level].bg;
  }, [level]);

  const borderColor = useMemo((): string => {
    return levelMapping[level].border;
  }, [level]);

  const icon = useMemo(() => {
    return levelMapping[level].icon;
  }, [level]);

  const textColor = useMemo(() => {
    return levelMapping[level].text;
  }, [level]);

  if (!showAlert) {
    return null;
  }

  const content = message ? (
    <p className={`text-sm font-medium ${textColor}`}>{message}</p>
  ) : (
    children
  );

  const handleDismiss = (event: React.MouseEvent<HTMLButtonElement>) => {
    setShowAlert(false);
    afterDismiss && afterDismiss(event);
  };

  return (
    <div className={`rounded-md ${backgroundColor} ${borderColor} p-4 shadow-sm`} role="alert">
      <div className="flex">
        <div className="shrink-0">
          <FontAwesomeIcon icon={icon} className={`${textColor} h-5 w-5`} aria-hidden="true" />
        </div>
        <div className="ml-3">{content}</div>
        {dismissable && (
          <div className="ml-auto pl-3">
            <div className="-mx-1.5 -my-1.5">
              <button
                type="button"
                onClick={handleDismiss}
                className={`${
                  level === 'default' ? 'text-gray-400' : textColor
                } hover:text-gray-500 focus:outline-hidden focus:ring-2 focus:ring-offset-2 focus:ring-offset-gray-50 focus:ring-${
                  level === 'default' ? 'gray' : level
                }-500`}>
                <span className="sr-only">Dismiss</span>

                <FontAwesomeIcon
                  icon={faTimes}
                  className={`${textColor} h-5 w-5`}
                  aria-hidden="true"
                />
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
